import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Car } from 'lucide-react'
import { AutocompleteInput, ColorPicker, InfoBox, ModernInput } from '../../ui'
import { FormField, StepCard, FormGrid, MileageInput, SelectField, SelectOption } from '../../form'
import { VehicleCategorySelector } from '../VehicleCategorySelector'
import { useCatalog } from '../../../hooks/useCatalog'
import { Brand, Model } from '../../../types'
import { useBranches } from '../../../hooks/useBranches'
import { buildModelYearOptions } from '../utils/year-options'

interface VehicleOptionItem {
  value: string
  label: string
  hex?: string
}

interface VehicleEditStep1BasicProps {
  formData: {
    category: string
    branchId?: string
    name: string
    brandId: string
    brandName: string
    modelId: string
    modelName: string
    condition: string
    manufactureYear: string
    modelYear: string
    color: string
    mileage: string
    motorcycleSecondaryColor?: string
  }
  errors: Record<string, string>
  vehicleOptions: {
    colors?: VehicleOptionItem[]
    conditions?: VehicleOptionItem[]
  }
  onInputChange: (field: string, value: string) => void
  initialVehicleData?: {
    category?: string
    brandId?: string
    brandName?: string
    modelId?: string
    modelName?: string
  }
}

export const VehicleEditStep1Basic: React.FC<VehicleEditStep1BasicProps> = ({
  formData,
  errors,
  vehicleOptions,
  onInputChange,
}) => {
  const { t } = useTranslation()
  const { brands, models, isLoading: catalogLoading, fetchBrands, fetchBrandModels, setModels } = useCatalog()
  const { branches, isLoading: isLoadingBranches } = useBranches()

  const [brandSearch, setBrandSearch] = useState('')
  const [modelSearch, setModelSearch] = useState('')
  const [selectedBrandLogo, setSelectedBrandLogo] = useState<string | undefined>(undefined)

  const activeBranches = useMemo(() => branches.filter((branch) => branch.isActive), [branches])
  const selectedBranch = useMemo(() => branches.find((branch) => branch.id === formData.branchId), [branches, formData.branchId])

  const allowedCategoryCodes = useMemo(() => {
    if (!selectedBranch) return undefined
    const raw = selectedBranch.allowedVehicleCategories
    if (!raw || raw.length === 0) return ['A', 'B', 'C', 'D']
    return [...raw]
  }, [selectedBranch])

  const currentYear = new Date().getFullYear()
  const selectedModel = useMemo(() => models.find((model) => model.id === formData.modelId), [formData.modelId, models])
  const modelYearOptions = useMemo(
    () => buildModelYearOptions(selectedModel, currentYear),
    [currentYear, selectedModel],
  )
  const manufactureYearOptions = useMemo(() => {
    if (!formData.modelYear) return []
    const modelYear = Number(formData.modelYear)
    if (Number.isNaN(modelYear)) return []
    return [
      { value: modelYear.toString(), label: modelYear.toString() },
      { value: (modelYear - 1).toString(), label: (modelYear - 1).toString() },
    ]
  }, [formData.modelYear])

  const branchOptions: SelectOption[] = useMemo(
    () => activeBranches.map((branch) => ({ value: branch.id, label: branch.name })),
    [activeBranches],
  )

  const brandOptions = useMemo(
    () => brands.map((brand) => ({ id: brand.id, label: brand.name, image: brand.logoUrl })),
    [brands],
  )

  const modelOptions = useMemo(
    () => models.map((model) => ({ id: model.id, label: model.name })),
    [models],
  )

  const colorOptions: VehicleOptionItem[] = vehicleOptions?.colors || []
  const conditionOptions: SelectOption[] = useMemo(() => {
    const items = vehicleOptions?.conditions || []
    return items.map((item) => ({ value: item.value, label: item.label }))
  }, [vehicleOptions])

  useEffect(() => {
    if (formData.brandName && brandSearch !== formData.brandName) {
      setBrandSearch(formData.brandName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.brandName])

  useEffect(() => {
    if (formData.modelName && modelSearch !== formData.modelName) {
      setModelSearch(formData.modelName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.modelName])

  useEffect(() => {
    if (formData.category) {
      void fetchBrands(formData.category)
    }
  }, [fetchBrands, formData.category])

  useEffect(() => {
    if (formData.brandId) {
      void fetchBrandModels(formData.brandId)
    }
  }, [fetchBrandModels, formData.brandId])

  useEffect(() => {
    if (!formData.branchId) return
    const codes = allowedCategoryCodes ?? ['A', 'B', 'C', 'D']
    if (codes.length === 1 && formData.category !== codes[0]) {
      onInputChange('category', codes[0])
    }
    if (formData.category && !codes.includes(formData.category)) {
      onInputChange('category', '')
      setBrandSearch('')
      setModelSearch('')
      setSelectedBrandLogo(undefined)
      setModels([])
    }
  }, [allowedCategoryCodes, formData.branchId, formData.category, onInputChange, setModels])

  const handleBrandSearchChange = (value: string) => {
    setBrandSearch(value)
    if (!value) {
      onInputChange('brandId', '')
      onInputChange('brandName', '')
      onInputChange('modelId', '')
      onInputChange('modelName', '')
      setModelSearch('')
      setModels([])
    }
  }

  const handleModelSearchChange = (value: string) => {
    setModelSearch(value)
    if (!value) {
      onInputChange('modelId', '')
      onInputChange('modelName', '')
    }
  }

  const handleBrandSelect = async (brand: Brand) => {
    if (!brand.id) {
      onInputChange('brandId', '')
      onInputChange('brandName', '')
      onInputChange('modelId', '')
      onInputChange('modelName', '')
      onInputChange('manufactureYear', '')
      onInputChange('modelYear', '')
      onInputChange('name', '')
      setBrandSearch('')
      setModelSearch('')
      setSelectedBrandLogo(undefined)
      setModels([])
      return
    }

    onInputChange('brandId', brand.id)
    onInputChange('brandName', brand.label || brand.name || '')
    onInputChange('modelId', '')
    onInputChange('modelName', '')
    onInputChange('manufactureYear', '')
    onInputChange('modelYear', '')
    setBrandSearch(brand.label || brand.name || '')
    setModelSearch('')
    setSelectedBrandLogo(brand.logoUrl)
    setModels([])
    await fetchBrandModels(brand.id)
  }

  const handleModelSelect = (model: Model) => {
    if (!model.id) {
      onInputChange('modelId', '')
      onInputChange('modelName', '')
      onInputChange('manufactureYear', '')
      onInputChange('modelYear', '')
      onInputChange('name', '')
      setModelSearch('')
      return
    }

    onInputChange('modelId', model.id)
    onInputChange('modelName', model.label || model.name || '')
    onInputChange('manufactureYear', '')
    onInputChange('modelYear', '')
    setModelSearch(model.label || model.name || '')
  }

  useEffect(() => {
    if (formData.brandId && formData.modelId && formData.modelYear) {
      const name = `${formData.brandName} ${formData.modelName} ${formData.modelYear}`
      if (formData.name !== name) {
        onInputChange('name', name)
      }
    }
  }, [formData.brandId, formData.brandName, formData.modelId, formData.modelName, formData.modelYear, formData.name, onInputChange])

  const handleGenerateName = () => {
    if (formData.brandName && formData.modelName && formData.modelYear) {
      onInputChange('name', `${formData.brandName} ${formData.modelName} ${formData.modelYear}`)
    }
  }

  return (
    <>
      <SelectField
        label={t('vehicleRegistration.basic.branchLabel')}
        value={formData.branchId || ''}
        options={branchOptions}
        onChange={(value) => onInputChange('branchId', value)}
        placeholder={isLoadingBranches ? t('vehicleRegistration.basic.branchLoadingPlaceholder') : t('vehicleRegistration.basic.branchPlaceholder')}
      />

      {activeBranches.length === 0 && !isLoadingBranches && (
        <InfoBox variant="warning">{t('vehicleRegistration.basic.noBranchesWarning')}</InfoBox>
      )}

      {!formData.branchId && activeBranches.length > 0 && (
        <InfoBox variant="info">{t('vehicleRegistration.basic.noBranchSelectedInfo')}</InfoBox>
      )}

      <VehicleCategorySelector
        selectedCategory={formData.category}
        allowedCategoryCodes={allowedCategoryCodes}
        disabled={!formData.branchId}
        onCategoryChange={(category) => {
          onInputChange('category', category)
          setBrandSearch('')
          setModelSearch('')
          setSelectedBrandLogo(undefined)
          setModels([])
        }}
      />

      {errors.category && <InfoBox variant="danger">⚠️ {errors.category}</InfoBox>}

      {formData.category && (
        <StepCard title={t('vehicleRegistration.basic.title')} icon={Car}>
          <FormField label={t('vehicleRegistration.basic.nameLabel')} required error={errors.name} hint={t('vehicleRegistration.basic.nameHint')}>
            <div className="flex gap-2">
              <div className="flex-1">
                <ModernInput
                  value={formData.name}
                  onChange={(e) => onInputChange('name', e.target.value)}
                  placeholder={t('vehicleRegistration.basic.namePlaceholder')}
                  disabled
                  leftIcon={<Car className="h-5 w-5 text-muted-foreground" />}
                />
              </div>
              <button
                type="button"
                onClick={handleGenerateName}
                className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-md transition-colors whitespace-nowrap"
                title={t('vehicleRegistration.basic.generateNameTitle')}
              >
                {t('vehicleRegistration.basic.generateName')}
              </button>
            </div>
          </FormField>

          <FormGrid columns={2}>
            <FormField label={t('vehicleRegistration.basic.brandLabel')} required error={errors.brand}>
              <AutocompleteInput
                value={brandSearch}
                options={brandOptions}
                selectedId={formData.brandId}
                onChange={handleBrandSearchChange}
                onSelect={(brand) => void handleBrandSelect(brand as Brand)}
                placeholder={t('vehicleRegistration.basic.brandPlaceholder')}
                loading={catalogLoading}
                emptyMessage={t('vehicleRegistration.basic.brandEmpty')}
              />
            </FormField>

            <FormField label={t('vehicleRegistration.basic.modelLabel')} required error={errors.model}>
              <AutocompleteInput
                value={modelSearch}
                options={modelOptions}
                selectedId={formData.modelId}
                onChange={handleModelSearchChange}
                onSelect={(model) => handleModelSelect(model as Model)}
                placeholder={formData.brandId ? t('vehicleRegistration.basic.modelPlaceholder') : t('vehicleRegistration.basic.modelSelectBrandFirst')}
                disabled={!formData.brandId}
                emptyMessage={t('vehicleRegistration.basic.modelEmpty')}
              />
            </FormField>
          </FormGrid>

          <FormGrid columns={2}>
            <SelectField
              label={t('vehicleRegistration.basic.conditionLabel')}
              value={formData.condition}
              options={conditionOptions}
              onChange={(value) => {
                if (value === 'new_vehicle') {
                  const year = currentYear.toString()
                  onInputChange('condition', value)
                  onInputChange('manufactureYear', year)
                  onInputChange('modelYear', year)
                  onInputChange('mileage', '0')
                  if (formData.brandName && formData.modelName) {
                    onInputChange('name', `${formData.brandName} ${formData.modelName} ${year}`)
                  }
                  return
                }

                onInputChange('condition', value)
              }}
              placeholder={t('vehicleRegistration.basic.conditionPlaceholder')}
              required
              error={errors.condition}
            />

            <SelectField
              label={t('vehicleRegistration.basic.modelYearLabel')}
              value={formData.modelYear}
              options={modelYearOptions}
              onChange={(value) => {
                onInputChange('modelYear', value)
                if (value) {
                  onInputChange('manufactureYear', value)
                  if (formData.brandName && formData.modelName) {
                    onInputChange('name', `${formData.brandName} ${formData.modelName} ${value}`)
                  }
                }
              }}
              placeholder={t('vehicleRegistration.basic.modelYearPlaceholder')}
              required
              disabled={formData.condition === 'new_vehicle'}
              error={errors.modelYear}
            />

            <SelectField
              label={t('vehicleRegistration.basic.manufactureYearLabel')}
              value={formData.manufactureYear}
              options={manufactureYearOptions}
              onChange={(value) => onInputChange('manufactureYear', value)}
              placeholder={
                !formData.modelYear ? t('vehicleRegistration.basic.modelYearPlaceholder') : t('vehicleRegistration.basic.manufactureYearPlaceholder')
              }
              required
              disabled={!formData.modelYear || formData.condition === 'new_vehicle'}
              error={errors.manufactureYear}
              hint={t('vehicleRegistration.basic.manufactureYearHint')}
            />

            <FormField label={formData.category === 'A' ? t('vehicleRegistration.basic.primaryColorLabel') : t('vehicleRegistration.basic.colorLabel')} error={errors.color}>
              <ColorPicker
                value={formData.color}
                options={colorOptions}
                onChange={(color) => onInputChange('color', color.value)}
                placeholder={t('vehicleRegistration.basic.colorPlaceholder')}
              />
            </FormField>

            {formData.category === 'A' && (
              <FormField label={t('vehicleRegistration.basic.secondaryColorLabel')}>
                <ColorPicker
                  value={formData.motorcycleSecondaryColor || ''}
                  options={colorOptions}
                  onChange={(color) => onInputChange('motorcycleSecondaryColor', color.value)}
                  placeholder={t('vehicleRegistration.basic.colorOptionalPlaceholder')}
                />
              </FormField>
            )}

            <FormField
              label={formData.condition !== 'new_vehicle' ? t('vehicleRegistration.basic.mileageRequiredHint') : t('vehicleRegistration.basic.mileageLabel')}
              error={errors.mileage}
              hint={formData.condition === 'new_vehicle' ? t('vehicleRegistration.basic.mileageAutoHint') : undefined}
            >
              <MileageInput
                value={formData.mileage}
                onChange={(value) => onInputChange('mileage', value)}
                placeholder={formData.condition === 'new_vehicle' ? t('vehicleRegistration.basic.mileageZeroPlaceholder') : t('vehicleRegistration.basic.mileagePlaceholder')}
                disabled={formData.condition === 'new_vehicle'}
              />
            </FormField>
          </FormGrid>
        </StepCard>
      )}
    </>
  )
}
